/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IFieldValidationError } from "../components/Fields/FieldTypes";
import { IBlueprintSchemaValidatorHandler } from "@adapptio/lib-blueprint/src/Validator/IBlueprintSchemaValidator";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TTranslateHookFn, useTranslate } from "@adapptio/lib-components";
import { isDeepEqual } from "@adapptio/lib-shared";
import { IBlueprintSchemaValidationError } from "@adapptio/lib-blueprint";
import { useDeepCompareCallback } from "use-deep-compare";

export interface IFieldState<TValue> {
	id: string;
	value: TValue;
	validationErrors: IFieldValidationError[];
	pristine: boolean;
}

export type TFieldValidator<TValue> = (value: TValue) => IBlueprintSchemaValidationError[];

export interface IFieldConfig<TValue> {
	defaultValue: TValue;
	validators?: Array<IBlueprintSchemaValidatorHandler<TValue> | TFieldValidator<TValue>>;
}

export type TGetFieldConfigValueType<TConfig> = TConfig extends IFieldConfig<infer A> ? A : never;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TFieldConfigProps = { [K: string]: IFieldConfig<any> };

export type TFieldGroupState<TConfigProps extends TFieldConfigProps> = {
	[K in keyof TConfigProps]: IFieldState<TGetFieldConfigValueType<TConfigProps[K]>>;
};

export type TFieldGroupSetters<TConfigProps extends TFieldConfigProps> = {
	[K in keyof TConfigProps]: (value: TGetFieldConfigValueType<TConfigProps[K]>) => void;
};

export interface IFieldGroup {
	reset: () => void;
	valid: boolean;
	pristine: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function validateField<TConfig extends IFieldConfig<any>>(
	config: TConfig,
	value: TGetFieldConfigValueType<TConfig>,
	t: TTranslateHookFn
) {
	if (!config.validators) {
		return;
	}

	const _errors: IBlueprintSchemaValidationError[] = [];

	for (let i = 0; i < config.validators.length; i++) {
		const validator = config.validators[i];

		if (typeof validator === "function") {
			_errors.push(...validator(value));
		} else {
			_errors.push(...validator.validate(value));
		}
	}

	return _errors.map((error) => {
		return {
			message: error.metaData?.translationTerm
				? t("editor", error.metaData.translationTerm, error.metaData?.args)
				: error.message
		};
	});
}

/**
 * Hook to create a state management for custom fields
 *
 * @param config Fields config
 */
export function useFieldState<TConfigProps extends TFieldConfigProps>(
	config: TConfigProps,
	deps: React.DependencyList = []
): [
	fieldState: TFieldGroupState<TConfigProps>,
	fieldSetters: TFieldGroupSetters<TConfigProps>,
	fieldGroup: IFieldGroup
] {
	const t = useTranslate();
	const prevDeps = useRef(deps);

	const getInitialState = useDeepCompareCallback(() => {
		const _state = {} as TFieldGroupState<TConfigProps>;

		for (const k in config) {
			_state[k] = {
				id: k,
				value: config[k].defaultValue,
				validationErrors: [],
				pristine: true
			};
		}

		return _state;
	}, [ config ]);

	const [ state, setState ] = useState(getInitialState);

	const setters = useMemo(() => {
		const _setters = {} as TFieldGroupSetters<TConfigProps>;

		const createSetter = (id: string) => {
			return (value: TGetFieldConfigValueType<TConfigProps[typeof id]>) => {
				setState((prevState) => {
					const newState = {
						...prevState
					};

					newState[id].value = value;
					newState[id].validationErrors = validateField(config[id], value, t);
					newState[id].pristine = false;

					return newState;
				});
			};
		};

		for (const k in config) {
			_setters[k] = createSetter(k);
		}

		return _setters;
	}, [ config, ...deps ]);

	useEffect(() => {
		if (!prevDeps.current) {
			prevDeps.current = deps;

			return;
		}

		if (!isDeepEqual(prevDeps.current, deps)) {
			setState((prevState) => {
				const newState = Object.keys(prevState).reduce((acc, id) => {
					return {
						...acc,
						[id]: {
							...prevState[id],
							validationErrors: prevState[id].pristine
								? []
								: validateField(config[id], prevState[id].value, t)
						}
					};
				}, {} as TFieldGroupState<TConfigProps>);

				return newState;
			});

			prevDeps.current = deps;
		}
	}, [ config, ...deps ]);

	const reset = useCallback(() => {
		setState(getInitialState());
	}, [ setState, getInitialState ]);

	const fieldGroup: IFieldGroup = useMemo(() => {
		let valid = true;
		let pristine = true;

		const stateKeys = Object.keys(state);

		for (let i = 0; i < stateKeys.length; i++) {
			const stateKey = stateKeys[i];
			const fieldState = state[stateKey];

			if (valid) {
				const validationErrors = validateField(config[stateKey], fieldState.value, t);
				valid = !(validationErrors?.length > 0);
			}

			pristine = pristine && fieldState.pristine;

			if (!valid && !pristine) {
				break;
			}
		}

		return {
			reset: reset,
			valid: valid,
			pristine: pristine
		};
	}, [ state, reset, config ]);

	return [ state, setters, fieldGroup ];
}
